import { PrePublicPortalWlFormService } from "@prePublicPortalWestLindsay/services/PrePublicPortalWlFormService";
import { Button, H2, InsetText, LoadingBox } from "govuk-react";
import React, { memo, useCallback, useState } from "react";
import { useForm } from "../hooks/formContext";
import { SummaryStepFields } from "@publicMirage/components/govReact/SummaryStepFields";
import {
    PrePpWlDescriptionOfDevelopmentAlterationDevelopmentTypes,
    PrePpWlDescriptionOfDevelopmentDevelopmentTypes,
    PrePpWlFormKeyToLabel,
    PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes,
} from "@shared/prePublicPortalWestLindsay/contracts/IPrePpWlForm";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { PrePublicPortalWLRoutingService } from "@prePublicPortalWestLindsay/services/PrePublicPortalWLRoutingService";
import { PrPpWlFormManager } from "@shared/prePublicPortalWestLindsay/managers/PrePpWlFormManager";
import { PrePublicPortalWlConstants } from "@shared/prePublicPortalWestLindsay/constants";
import Box from "@mui/material/Box";

const service = new PrePublicPortalWlFormService();
const routing = new PrePublicPortalWLRoutingService();

export const PrePpWlSubmitStep = memo(() => {
    const { formState, previous } = useForm();
    const [submitIsLoading, setSubmitIsLoading] = useState(false);
    const navigate = useStableNavigate();
    const submitHandler = useCallback(async () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setSubmitIsLoading(true);
        const relUrl = routing.toRoute(routing.Root);
        const result = await service.submitForm(formState, relUrl);
        setSubmitIsLoading(false);
        if (result.errorMsg) {
            navigate(routing.FormSubmitErrorRoute(), { state: { ...result } });
        } else if (result.name) {
            navigate(routing.FormSubmitSuccessRoute(), { state: { ...result } });
        } else {
            navigate(routing.FormSubmitErrorRoute(), { state: { errorMsg: "An error occurred while submitting the form." } });
        }
    }, [formState, navigate]);

    const applicantFields = {
        [PrePpWlFormKeyToLabel["applicantFirstName"]]: formState.applicantFirstName.value,
        [PrePpWlFormKeyToLabel["applicantSurname"]]: formState.applicantSurname.value,
        [PrePpWlFormKeyToLabel["applicantEmail"]]: formState.applicantEmail.value,
        [PrePpWlFormKeyToLabel["applicantPostTown"]]: formState.applicantPostTown.value,
        [PrePpWlFormKeyToLabel["applicantPostcode"]]: formState.applicantPostcode.value,
        [PrePpWlFormKeyToLabel["applicantAddressLine1"]]: formState.applicantAddressLine1.value,
        [PrePpWlFormKeyToLabel["applicantAddressLine2"]]: formState.applicantAddressLine2.value,
        [PrePpWlFormKeyToLabel["applicantAddressLine3"]]: formState.applicantAddressLine3.value,
    };
    const agentFields = {
        [PrePpWlFormKeyToLabel["agentFirstName"]]: formState.agentFirstName.value,
        [PrePpWlFormKeyToLabel["agentSurname"]]: formState.agentSurname.value,
        [PrePpWlFormKeyToLabel["agentEmail"]]: formState.agentEmail.value,
        [PrePpWlFormKeyToLabel["agentPostTown"]]: formState.agentPostTown.value,
        [PrePpWlFormKeyToLabel["agentPostcode"]]: formState.agentPostcode.value,
        [PrePpWlFormKeyToLabel["agentAddressLine1"]]: formState.agentAddressLine1.value,
        [PrePpWlFormKeyToLabel["agentAddressLine2"]]: formState.agentAddressLine2.value,
        [PrePpWlFormKeyToLabel["agentAddressLine3"]]: formState.agentAddressLine3.value,
    };
    const descriptionOfDevelopmentFields = {
        [PrePpWlFormKeyToLabel["typeOfDevelopment"]]: formState.typeOfDevelopment.value,
    };
    const typeOfDevelopmentFields = {
        [PrePpWlFormKeyToLabel["developmentScale"]]: formState.developmentScale.value,
        ...getTypeOfDevelopmentFields(),
    };
    const siteDetailsFields = {
        [PrePpWlFormKeyToLabel["currentUseOfTheSite"]]: formState.currentUseOfTheSite.value,
        [PrePpWlFormKeyToLabel["proposalAffectsAnyTrees"]]: formState.proposalAffectsAnyTrees.value,
        [PrePpWlFormKeyToLabel["siteLocationIsSameAsApplicant"]]: formState.siteLocationIsSameAsApplicant.value,
        [PrePpWlFormKeyToLabel["siteLocationPostTown"]]: formState.siteLocationPostTown.value,
        [PrePpWlFormKeyToLabel["siteLocationPostcode"]]: formState.siteLocationPostcode.value,
        [PrePpWlFormKeyToLabel["siteLocationAddressLine1"]]: formState.siteLocationAddressLine1.value,
        [PrePpWlFormKeyToLabel["siteLocationAddressLine2"]]: formState.siteLocationAddressLine2.value,
        [PrePpWlFormKeyToLabel["siteLocationAddressLine3"]]: formState.siteLocationAddressLine3.value,
    };
    const stageFields = {
        [PrePpWlFormKeyToLabel["enquiryStage"]]: formState.enquiryStage.value,
    };
    const additionalInformationFields = {
        [PrePpWlFormKeyToLabel["siteLocationDocuments"]]: formState.siteLocationDocuments.value,
        [PrePpWlFormKeyToLabel["hardCopySiteLocationPlansWillBeProvided"]]: formState.hardCopySiteLocationPlansWillBeProvided.value,
        [PrePpWlFormKeyToLabel["optionalDocuments"]]: formState.optionalDocuments.value,
        [PrePpWlFormKeyToLabel["hardCopyOptionalDocumentsWillBeProvided"]]: formState.hardCopyOptionalDocumentsWillBeProvided.value,
        [PrePpWlFormKeyToLabel["otherInformation"]]: formState.otherInformation.value,
    };
    const feeFields = {
        ...getFeeFields(),
    };
    const showAgentSection = !PrPpWlFormManager.isAgentDetailsEmpty(formState);

    return (
        <LoadingBox loading={submitIsLoading}>
            <H2>Summary</H2>
            <InsetText>
                <SummaryStepFields caption="Applicant Details" fields={applicantFields} />
            </InsetText>
            {showAgentSection ? (
                <InsetText>
                    <SummaryStepFields caption="Agent Details" fields={agentFields} />
                </InsetText>
            ) : null}

            <InsetText>
                <SummaryStepFields caption="Description of Development" fields={descriptionOfDevelopmentFields} />
            </InsetText>
            <InsetText>
                <SummaryStepFields caption="Service Description" fields={typeOfDevelopmentFields} />
            </InsetText>
            <InsetText>
                <SummaryStepFields caption="Site Details" fields={siteDetailsFields} />
            </InsetText>
            <InsetText>
                <SummaryStepFields caption="Stage" fields={stageFields} />
            </InsetText>
            <InsetText>
                <SummaryStepFields caption="Additional Information" fields={additionalInformationFields} />
            </InsetText>
            <InsetText>
                <SummaryStepFields caption="Fee" fields={feeFields} />
            </InsetText>
            <Box display="flex" justifyContent="space-between">
                <Button onClick={previous}>Previous</Button>
                <Button onClick={submitHandler}>Submit</Button>
            </Box>
        </LoadingBox>
    );

    function getFeeFields() {
        if (!formState.isCustomFee.value) {
            return {
                [PrePpWlFormKeyToLabel["feeAmount"]]: formState.feeAmount.value,
            };
        }
        return {
            [PrePpWlFormKeyToLabel["feeMessage"]]: PrePublicPortalWlConstants.CustomFeeContactMessage,
        };
    }

    function getTypeOfDevelopmentFields(): { [key: string]: string } {
        const fields: { [key: string]: string } = {};
        const showAdditionalGrossFloorSpaceTypes = [PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.EXTENSION, PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.NEW_BUILD];
        const showAlterationDimensionsTypes = [
            PrePpWlDescriptionOfDevelopmentAlterationDevelopmentTypes.EXTENSION,
            PrePpWlDescriptionOfDevelopmentAlterationDevelopmentTypes.OUTBUILDING,
        ];
        const showAdditionalGrossFloorSpace = showAdditionalGrossFloorSpaceTypes.includes(formState.developmentScale.value as any);
        const showAlterationDimensions = showAlterationDimensionsTypes.includes(formState.developmentScale.value as any);
        if (formState.typeOfDevelopment.value === PrePpWlDescriptionOfDevelopmentDevelopmentTypes.NEW_DWELLINGS) {
            fields["Total number of units proposed"] = formState.totalNumberOfUnitsProposed.value;
            fields["Type of new build"] = formState.typeOfNewBuild.value;
        }
        if (formState.typeOfDevelopment.value === PrePpWlDescriptionOfDevelopmentDevelopmentTypes.RETAIL_OFFICE_INDUSTRIAL) {
            if (showAdditionalGrossFloorSpace) {
                fields["Additional gross floor space"] = formState.additionalGrossFloorSpace.value;
            }

            fields["Additional full time employees"] = formState.additionalFullTimeEmployees.value;
            fields["Additional part time employees"] = formState.additionalPartTimeEmployees.value;
            fields["Estimated number of vehicular trips"] = formState.estimatedNumberOfVehicularTrips.value;
            if (formState.developmentScale.value === PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.CHANGE_OF_USE) {
                fields["Non-residential change of use"] = formState.nonResidentialChangeOfUse.value;
            }
            if (formState.developmentScale.value === PrePpWlTypeOfRetailOfficeIndustrialDevelopmentTypes.ALTERATIONS) {
                fields["Alterations description"] = formState.alterationsDescription.value;
            }
        }
        if (formState.typeOfDevelopment.value === PrePpWlDescriptionOfDevelopmentDevelopmentTypes.OTHER) {
            fields["Other development description"] = formState.otherDevelopmentDescription.value;
        }
        if (showAlterationDimensions) {
            fields["Alteration width"] = formState.alterationWidth.value;
            fields["Alteration length"] = formState.alterationLength.value;
            fields["Alteration height to eaves"] = formState.alterationHeightToEaves.value;
            fields["Alteration height"] = formState.alterationHeight.value;
        }
        return fields;
    }
});
